import { Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { ModalComponent } from "../../../shared/modal/modal.component";
import { PatientListItem } from "../../../core/models/patient-list.model";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { PatientService } from "../../../core/services/patient.service";
import { LogHelper } from "../../../core/helpers/log.helper";
import { AlertService } from "../../../shared/alert/alert.service";
import { PatientDetail } from "../../../core/models/patient-detail.model";
import { PatientPreApprovalCheck } from "../../../core/models/patient-pre-approval-check.model";
import { SiteDropdownComponent } from "../../../shared/site-dropdown/site-dropdown.component";

@Component({
  selector: 'app-patient-approve-modal',
  templateUrl: './patient-approve-modal.component.html',
  styleUrl: './patient-approve-modal.component.scss'
})
export class PatientApproveModalComponent {
  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('mergeModal') mergeModal: ModalComponent;
  @ViewChild('siteSelect') siteSelect: SiteDropdownComponent;

  @Output() approved = new EventEmitter();

  private readonly patientService = inject(PatientService);
  private readonly alertService = inject(AlertService);

  form: UntypedFormGroup = new UntypedFormGroup({
    patientTrialId: new UntypedFormControl('', Validators.required),
    trialId: new UntypedFormControl('', Validators.required),
    patientCode: new UntypedFormControl('', [Validators.required, Validators.maxLength(16)]),
    siteId: new UntypedFormControl('', Validators.required),
    patientConsent: new UntypedFormControl(false, Validators.requiredTrue),
    showPatientConsentCheckbox: new UntypedFormControl(true),
    patientConsentDate: new UntypedFormControl(''),
    patientConsentType: new UntypedFormControl('')
  });
  approvingPatient: PatientListItem;
  isApprovalFormProcessing = false;
  patientApprovalCheck: PatientPreApprovalCheck;

  reset() {
    this.form.reset();
    this.modal.hide();
    this.mergeModal.hide();
  }

  show(patient: PatientDetail | PatientListItem) {
    this.form.reset();

    this.form.patchValue({
      patientTrialId: patient.patientTrialId,
      siteId: patient.siteId,
      trialId: patient.trialId,
      patientCode: patient.patientCode,
      patientConsent: patient.consent,
      showPatientConsentCheckbox: !patient.consent,
      patientConsentDate: patient.consentDate,
      patientConsentType: patient.consentType.toLowerCase()
    });

    console.log(this.form);

    this.modal.show();
  }

  approvePatientNoMerge() {
    this.isApprovalFormProcessing = true;
    this.mergeModal.hide();
    this.patientApprovalCheck = null;
    this.approvePatient(
      this.approvingPatient.patientTrialId,
      this.form.get('patientCode').value,
      this.form.get('siteId').value,
      this.form.get('patientConsent').value
    );
  }

  mergePatient() {
    this.isApprovalFormProcessing = true;
    this.patientApprovalCheck = null;
    this.mergeAndApprovePatient(
      this.approvingPatient.patientTrialId,
      this.form.get('patientCode').value,
      this.form.get('siteId').value,
      this.form.get('patientConsent').value
    );
  }

  approvePatient(patientTrialId: string, patientCode: string, siteId: string, patientConsent: boolean) {
    this.patientService.approvePatient(
      patientTrialId,
      patientCode,
      siteId,
      patientConsent
    ).subscribe({
      next: () => {
        this.approved.emit();
        this.isApprovalFormProcessing = false;
        this.reset();
      },
      error: (error) => {
        LogHelper.log(error);
        this.isApprovalFormProcessing = false;
        this.alertService.showErrorAlert(error);
      }
    });
  }

  mergeAndApprovePatient(patientTrialId: string, patientCode: string, siteId: string, patientConsent: boolean) {
    this.patientService.mergeAndApprovePatient(
      patientTrialId,
      patientCode,
      siteId,
      patientConsent
    ).subscribe({
      next: () => {
        this.approved.emit();
        this.isApprovalFormProcessing = false;
        this.reset()
      },
      error: (error) => {
        LogHelper.log(error);
        this.isApprovalFormProcessing = false;
        this.alertService.showErrorAlert(error);
      }
    });
  }

  onSubmitForm() {
    if (this.form.valid) {
      this.isApprovalFormProcessing = true;

      // Pre-approve the patient to make sure the patient code isn't in use on the trial
      this.patientService.preApprovePatient(
        this.form.get('patientTrialId').value,
        this.form.get('patientCode').value,
        this.form.get('siteId').value,
        this.form.get('patientConsent').value
      ).subscribe({
        next: (rsp) => {
          if (rsp.patientExists) {
            // Patient code is in use, does the admin want to merge the patients?
            this.patientApprovalCheck = rsp;
            this.mergeModal.show();
            this.isApprovalFormProcessing = false;
          } else {
            // Approve the patient, the patient code isn't being used on the trial
            this.approvePatient(
              this.form.get('patientTrialId').value,
              this.form.get('patientCode').value,
              this.form.get('siteId').value,
              this.form.get('patientConsent').value);
          }
        },
        error: (error) => {
          LogHelper.log(error);
          this.isApprovalFormProcessing = false;
          this.alertService.showErrorAlert(error);
        }
      });
    }
  }
}
